// kanjo/Scan.jsx — receipt/invoice scan flow wired to OCR pipeline.
//
// Reached from the Receipts (+) sheet, which offers three entry points:
//   /receipts/scan            → ScanCapture    (camera AND upload on one screen)
//   /receipts/scan/upload     → ScanCapture    (same screen; the sheet's upload shortcut)
//   /receipts/manual          → ManualEntry    (no OCR — amount/tax/delivery/date by hand)
// Camera and upload both continue through the shared pipeline:
//   /receipts/scan/processing → ScanProcessing (upload + OCR poll, with preview)
//   /receipts/scan/confirm    → ScanConfirm    (review + edit + save)
//   /receipts/scan/done       → ScanDone       (success + insights; ManualEntry lands here too)
//
// Cross-screen state lives on window.__kanjoScan to avoid serialising blobs
// through the URL or sessionStorage. Cleared after a successful save.

// Below this, an OCR-read tax/freight charge is routine (small sales tax on a
// few taxable items, a small delivery fee) and doesn't warrant the "unconfirmed
// charge" banner on the confirm screen — see the `unconfirmedFees` check there.
const UNCONFIRMED_FEE_THRESHOLD_CENTS = 500;

// previewUrl is always an IMAGE object URL safe to drop in an <img> (the first
// captured page). It's separate from blobUrl because blobUrl may be a PDF (for
// multi-page invoices), which an <img> can't render.
window.__kanjoScan = window.__kanjoScan || { blob: null, blobUrl: null, previewUrl: null, pageCount: 0, ocr: null, savedInvoice: null };

function scanReset() {
  const s = window.__kanjoScan;
  if (s.blobUrl) { try { URL.revokeObjectURL(s.blobUrl); } catch {} }
  if (s.previewUrl) { try { URL.revokeObjectURL(s.previewUrl); } catch {} }
  window.__kanjoScan = { blob: null, blobUrl: null, previewUrl: null, pageCount: 0, ocr: null, savedInvoice: null };
}

// ── Leave-scan confirmation ──────────────────────────────────
// Shown when the user tries to back out of a scan in progress. Backing out
// discards the captured pages, so we confirm and then send them to the receipts
// home — never back into the camera (which previously re-fired the scan).
function ConfirmLeaveScan({ onStay, onLeave }) {
  return (
    <div role="dialog" aria-modal="true" aria-label="Discard scan?"
         onClick={onStay}
         style={{
           position: 'absolute', inset: 0, zIndex: 50,
           background: 'rgba(0,0,0,0.5)',
           display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
         }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 420,
        background: 'var(--surface)', color: 'var(--ink)',
        borderTopLeftRadius: 20, borderTopRightRadius: 20,
        padding: '24px 22px calc(env(safe-area-inset-bottom, 0) + 22px)',
        boxShadow: '0 -12px 32px rgba(0,0,0,0.22)',
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Discard this scan?</div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 22 }}>
          You'll lose the captured pages and need to scan again. We won't save anything yet.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn lg" onClick={onLeave}
                  style={{ width: '100%', background: 'var(--danger-fill)', color: 'var(--on-danger-fill)', border: 'none', cursor: 'pointer' }}>
            Discard and go back
          </button>
          <button className="btn ghost lg" onClick={onStay} style={{ width: '100%', cursor: 'pointer' }}>
            Keep scanning
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Image helpers: downscale phone photos + assemble pages into one PDF ──────
// Phone photos are ~4000px/3-4MB; OCR only needs legible text, so we downscale
// to keep uploads small and under the 20MB presign cap, then bundle multiple
// pages into a single PDF so the whole invoice is OCR'd + extracted as ONE doc.
function downscaleToJpeg(file, maxDim = 2000, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('downscale failed'))), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('could not read image')); };
    img.src = url;
  });
}

async function assemblePdf(blobs) {
  if (!window.PDFLib) throw new Error('PDF support not loaded — reload and retry');
  const { PDFDocument } = window.PDFLib;
  const pdf = await PDFDocument.create();
  for (const b of blobs) {
    const img = await pdf.embedJpg(new Uint8Array(await b.arrayBuffer()));
    const page = pdf.addPage([img.width, img.height]);
    page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
  }
  return new Blob([await pdf.save()], { type: 'application/pdf' });
}

// ── State 1: capture pages (camera + upload on ONE screen) ───
// One screen, one mental model: pick a source (Take photo / Upload file), see
// the pages you've added, then tap Scan. The old flow opened what looked like a
// live camera viewfinder that never lit up — the shutter just launched the OS
// camera — and finished with a "Done" button that read like "I'm finished
// here", not "read this invoice". Both were a standing source of hesitation
// even for daily users, so the viewfinder is gone and the action says Scan.
const CamIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8.5A1.5 1.5 0 014.5 7h2.2l1.1-1.8A1.5 1.5 0 019.1 4.5h5.8a1.5 1.5 0 011.3.7L17.3 7h2.2A1.5 1.5 0 0121 8.5v9a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 17.5z"/>
    <circle cx="12" cy="13" r="3.4"/>
  </svg>
);
const UploadIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 16v2a2 2 0 002 2h10a2 2 0 002-2v-2"/><path d="M12 3v12M7 8l5-5 5 5"/>
  </svg>
);

// A source button (camera / upload). Both are the same size and weight — neither
// is the primary action; the primary action is Scan, at the bottom.
function SourceButton({ icon, label, hint, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
            style={{
              flex: 1, minWidth: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              padding: '16px 10px', borderRadius: 16,
              background: 'var(--surface)', color: 'var(--ink)',
              border: '1px solid var(--surface-3)',
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'default' : 'pointer',
            }}>
      <span style={{
        width: 44, height: 44, borderRadius: 22,
        background: 'var(--brand-soft)', color: 'var(--brand)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</span>
      <span style={{ fontSize: 15, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 12, color: 'var(--ink-2)', textAlign: 'center', lineHeight: 1.35 }}>{hint}</span>
    </button>
  );
}

function ScanCapture({ dark = false, persona = 'owner' }) {
  const nav = React.useContext(KanjoNav);
  const camRef = React.useRef(null);
  const fileRef = React.useRef(null);
  // A page is either a downscaled JPEG we can show, or one picked PDF (already a
  // whole document — shown as a placeholder tile since an <img> can't render it).
  const [pages, setPages] = React.useState([]);   // [{ id, blob, url, kind }]
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);   // plan/upsell messages
  // Guard the back arrow: leaving with added-but-unprocessed pages throws them
  // away, so confirm first. No pages → leave freely.
  const [confirmLeave, setConfirmLeave] = React.useState(false);
  const leave = () => {
    pages.forEach((p) => { if (p.url) { try { URL.revokeObjectURL(p.url); } catch {} } });
    nav.navigate('/receipts');
  };
  // Multi-page assembly and file upload are paid; Free is one camera page per
  // scan (which also blocks pre-crafted multi-page composites gaming the quota).
  // Default to the restricted tier until claims load. Server enforces this too.
  const [tier, setTier] = React.useState('free');
  React.useEffect(() => {
    window.kanjo?.auth?.currentClaims?.()
      .then((c) => setTier((c && c['custom:tier']) || 'free'))
      .catch(() => setTier('free'));
  }, []);
  // Team viewers are read-only — scanning is disabled for them.
  const [viewer, setViewer] = React.useState(false);
  React.useEffect(() => {
    window.kanjo?.scale?.account?.currentAccount?.().then((a) => setViewer(a?.role === 'viewer')).catch(() => {});
  }, []);
  // A ZIP is required before scanning — it's how prices get grouped by area.
  // null = still checking; true/false = has a valid 5-digit ZIP or not.
  const [hasZip, setHasZip] = React.useState(null);
  React.useEffect(() => {
    window.kanjo?.scale?.profile?.getProfile?.()
      .then((p) => setHasZip(/^\d{5}$/.test((p && p.zip) || '')))
      // Don't gate on a transient lookup failure — let the user proceed; the
      // server's presign gate is the real enforcement and surfaces ZIP_REQUIRED.
      .catch(() => setHasZip(true));
  }, []);
  const paid = tier !== 'free';
  const hasPdf = pages.some((p) => p.kind === 'pdf');
  const atPageCap = (!paid && pages.length >= 1) || hasPdf;

  const addImageFiles = async (files) => {
    setBusy(true); setError(null);
    try {
      const next = [];
      for (const f of files) {
        const blob = await downscaleToJpeg(f);
        next.push({ id: crypto.randomUUID(), blob, url: URL.createObjectURL(blob), kind: 'image' });
      }
      setPages((p) => [...p, ...next]);
    } catch (e) { setError(e.message || 'Could not add page.'); }
    finally { setBusy(false); }
  };

  const onCamCapture = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';                       // reset so the same shot can re-fire
    if (f) addImageFiles([f]);
  };

  // Upload accepts a PDF (already one document) or one/more page photos.
  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true); setError(null);
    try {
      const pdf = files.find((f) => f.type === 'application/pdf');
      if (pdf) {
        if (pages.length) { setError('Remove the pages you added first — a PDF is scanned on its own.'); return; }
        setPages([{ id: crypto.randomUUID(), blob: pdf, url: null, kind: 'pdf', name: pdf.name }]);
        return;
      }
      const images = files.filter((f) => f.type.startsWith('image/'));
      if (!images.length) { setError('Pick a PDF or image file.'); return; }
      await addImageFiles(images);
    } catch (err) { setError(err.message || 'Could not read the file.'); }
    finally { setBusy(false); }
  };

  const openCamera = () => {
    setNotice(null); setError(null);
    if (hasPdf) { setError('Remove the PDF to take photos instead.'); return; }
    if (atPageCap) { setNotice('Free plan: one page per scan. Upgrade for multi-page invoices.'); return; }
    camRef.current && camRef.current.click();
  };
  const openUpload = () => {
    setNotice(null); setError(null);
    if (!paid) { setNotice('Uploading a file is a paid feature. On Free, take a photo with the camera.'); return; }
    fileRef.current && fileRef.current.click();
  };

  const removePage = (id) => setPages((p) => {
    const gone = p.find((x) => x.id === id);
    if (gone && gone.url) { try { URL.revokeObjectURL(gone.url); } catch {} }
    return p.filter((x) => x.id !== id);
  });

  // previewBlob is an image we can show while OCR runs; pageCount drives the
  // "page N" badge. A picked PDF has no image preview (previewBlob omitted) —
  // ScanProcessing falls back to a document placeholder.
  function startDoc(blob, previewBlob, pageCount = 1) {
    scanReset();
    window.__kanjoScan.blob = blob;
    window.__kanjoScan.blobUrl = URL.createObjectURL(blob);
    if (previewBlob) window.__kanjoScan.previewUrl = URL.createObjectURL(previewBlob);
    window.__kanjoScan.pageCount = pageCount;
    nav.navigate('/receipts/scan/processing');
  }

  // The one action that reads the invoice. Single page → keep the JPEG (cheaper);
  // several → assemble one multi-page PDF so it's OCR'd as ONE document.
  const scan = async () => {
    if (!pages.length || busy) return;
    setBusy(true); setError(null);
    try {
      if (hasPdf) { startDoc(pages[0].blob); return; }
      const blob = pages.length === 1 ? pages[0].blob : await assemblePdf(pages.map((p) => p.blob));
      // First page is always an image, so it's a safe <img> preview even when
      // the document itself is assembled into a PDF.
      startDoc(blob, pages[0].blob, pages.length);
    } catch (e) { setError(e.message || 'Could not assemble document.'); setBusy(false); }
  };

  const gateShell = (body) => (
    <KShell dark={dark} tab="receipts" persona={persona} noNav={true}>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
        <KHeader back title="Scan invoice" onBack={() => nav.navigate('/receipts')} />
        {body}
      </div>
    </KShell>
  );

  // Read-only viewers cannot scan.
  if (viewer) {
    return gateShell(
      <div style={{ flex: 1, padding: '8px 28px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600 }}>Read-only access</div>
        <div style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, maxWidth: 300 }}>
          You're viewing the account owner's books. Scanning is available to the account owner.
        </div>
        <KLink to="/receipts" asButton style={{ width: '100%', maxWidth: 280 }}>
          <button className="btn primary lg" style={{ width: '100%' }}>Back to receipts</button>
        </KLink>
      </div>
    );
  }

  // ZIP gate: block scanning until a restaurant ZIP is set (server enforces too).
  if (hasZip === false) {
    return gateShell(
      <div style={{ flex: 1, padding: '8px 28px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 18 }}>
        <div style={{ width: 56, height: 56, borderRadius: 28, background: 'var(--brand-soft)', color: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-5.7 7-11a7 7 0 10-14 0c0 5.3 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Add your ZIP code first</div>
          <div style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, maxWidth: 300 }}>
            Kanjo groups prices by area (county). Set your restaurant ZIP code once, then you're ready to scan.
          </div>
        </div>
        <KLink to="/account" asButton style={{ width: '100%', maxWidth: 280 }}>
          <button className="btn primary lg" style={{ width: '100%' }}>Set ZIP code</button>
        </KLink>
        <KLink to="/receipts" asButton style={{ width: '100%', maxWidth: 280 }}>
          <button className="btn ghost lg" style={{ width: '100%' }}>Not now</button>
        </KLink>
      </div>
    );
  }

  const pageWord = pages.length === 1 ? 'page' : 'pages';

  return (
    <KShell dark={dark} tab="receipts" persona={persona} noNav={true}>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
        <KHeader back title="Scan invoice" onBack={() => (pages.length ? setConfirmLeave(true) : leave())} />

        {/* Source picker — the two ways to add a page, side by side */}
        <div style={{ display: 'flex', gap: 12, padding: '4px 20px 14px' }}>
          <SourceButton icon={CamIcon} label="Camera" hint={paid ? 'Photograph each page' : 'Photograph the page'}
                        onClick={openCamera} disabled={busy} />
          <SourceButton icon={UploadIcon} label="Upload" hint="PDF or photos on this device"
                        onClick={openUpload} disabled={busy} />
        </div>

        {/* Pages added so far */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 12px' }}>
          <div className="label" style={{ marginBottom: 8, paddingLeft: 4 }}>
            {pages.length ? `${pages.length} ${pageWord} ready` : 'No pages yet'}
          </div>
          <div className="card" style={{ padding: 14, minHeight: 168 }}>
            {pages.length === 0 ? (
              <div style={{ minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 8, color: 'var(--ink-2)' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 2h8l4 4v14a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"/><path d="M14 2v4h4M8 13h8M8 17h6"/>
                </svg>
                <div style={{ fontSize: 13.5, lineHeight: 1.45, maxWidth: 250 }}>
                  Take a photo of the invoice, or upload one you already have. It shows up here.
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {pages.map((p, i) => (
                  <div key={p.id} style={{ position: 'relative' }}>
                    {p.kind === 'pdf' ? (
                      <div aria-label={p.name || 'PDF document'} style={{
                        width: 92, height: 120, borderRadius: 10, background: 'var(--surface-3)', color: 'var(--ink-2)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h8l4 4v14a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"/><path d="M14 2v4h4M8 13h8M8 17h6"/></svg>
                        <span style={{ fontSize: 11.5, fontWeight: 600 }}>PDF</span>
                      </div>
                    ) : (
                      <img src={p.url} alt={`Page ${i + 1}`}
                           style={{ width: 92, height: 120, objectFit: 'cover', borderRadius: 10, display: 'block', background: 'var(--surface-3)' }}/>
                    )}
                    <span style={{ position: 'absolute', bottom: 4, left: 4, fontSize: 10.5, fontWeight: 600, color: 'var(--on-brand-fill)', background: 'var(--brand-fill)', borderRadius: 5, padding: '1px 5px' }}>{i + 1}</span>
                    <button onClick={() => removePage(p.id)} aria-label={`Remove page ${i + 1}`}
                            style={{ position: 'absolute', top: -6, right: -6, width: 24, height: 24, borderRadius: 12, background: 'var(--surface-3)', color: 'var(--ink)', border: '1px solid var(--surface)', cursor: 'pointer', fontSize: 13, lineHeight: '22px', padding: 0 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notice && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45 }}>{notice}</div>
              <KLink to="/account" asButton><button className="pill brand" style={{ border: 'none', cursor: 'pointer' }}>Upgrade</button></KLink>
            </div>
          )}
          {error && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: 'var(--danger)', lineHeight: 1.45 }}>{error}</div>}
        </div>

        {/* The action. "Scan" — reading the invoice is what happens next, which
            the old "Done" never said. */}
        <div style={{ padding: '10px 20px calc(env(safe-area-inset-bottom, 0) + 18px)', background: 'var(--bg)' }}>
          <button onClick={scan} disabled={!pages.length || busy} className="btn primary lg"
                  style={{ width: '100%', opacity: (!pages.length || busy) ? 0.5 : 1, cursor: (!pages.length || busy) ? 'default' : 'pointer' }}>
            {busy ? 'Working…' : pages.length > 1 ? `Scan ${pages.length} pages` : 'Scan'}
          </button>
        </div>
      </div>

      {/* Hidden inputs: the camera shutter and the file picker */}
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onCamCapture} disabled={busy}
             style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}/>
      <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple onChange={onFilePick} disabled={busy}
             style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}/>

      {confirmLeave && (
        <ConfirmLeaveScan
          onStay={() => setConfirmLeave(false)}
          onLeave={leave}
        />
      )}
    </KShell>
  );
}

// ── State 1.5: Upload + OCR poll ─────────────────────────────
function ScanProcessing({ dark = false, persona = 'owner' }) {
  const nav = React.useContext(KanjoNav);
  const [phase, setPhase] = React.useState('uploading'); // uploading | reading | error
  const [error, setError] = React.useState(null);
  const [errorCode, setErrorCode] = React.useState(null); // e.g. 'ZIP_REQUIRED'
  // Guard the back button: leaving mid-scan throws away the captured pages, so
  // confirm first, then return to the receipts home (never re-enter the camera).
  const [confirmLeave, setConfirmLeave] = React.useState(false);
  const leaveScan = () => { scanReset(); nav.navigate('/receipts'); };

  React.useEffect(() => {
    const session = window.__kanjoScan;
    if (!session || !session.blob) {
      // Nothing captured — bounce back to camera.
      nav.navigate('/receipts/scan');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setPhase('uploading');
        const mime = session.blob.type && /^(image\/(jpeg|png|webp)|application\/pdf)$/.test(session.blob.type)
          ? session.blob.type
          : 'image/jpeg';
        const { receiptId } = await window.kanjo.ocr.uploadReceiptImage(session.blob, mime);
        if (cancelled) return;
        // Cache the blob locally so the receipt-detail screen can show the
        // original to compare against the OCR output. Best-effort — failure
        // here shouldn't break the scan flow.
        try {
          const claims = await window.kanjo.auth.currentClaims().catch(() => null);
          if (claims?.sub) await window.kanjo.ocr.saveReceiptImage(receiptId, claims.sub, session.blob, mime);
        } catch (e) { console.warn('[Scan] cache image failed', e); }
        window.__kanjoScan.receiptId = receiptId;
        setPhase('reading');
        const ocr = await window.kanjo.ocr.pollOcrResult(receiptId, { timeoutMs: 60_000 });
        if (cancelled) return;
        window.__kanjoScan.ocr = ocr;
        nav.navigate('/receipts/scan/confirm');
      } catch (e) {
        if (cancelled) return;
        console.warn('[Scan] OCR pipeline failed', e);
        setError(e.message || 'Could not read the receipt.');
        setErrorCode(e.code || null);
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const session = window.__kanjoScan || {};
  const multiPage = (session.pageCount || 0) > 1;

  return (
    <KShell dark={dark} tab="receipts" persona={persona} noNav={true}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
        <KHeader back title="Reading…" onBack={() => setConfirmLeave(true)} />
        <div style={{ flex: 1, padding: '8px 24px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
          {/* Captured preview — image when we have one, PDF placeholder otherwise */}
          {(session.previewUrl || session.blob) && (
            <div style={{
              position: 'relative',
              width: '100%', maxWidth: 280,
              borderRadius: 16, overflow: 'hidden',
              boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
              background: 'var(--surface-3)',
            }}>
              {session.previewUrl ? (
                <img src={session.previewUrl} alt="Captured receipt"
                     style={{ width: '100%', display: 'block', maxHeight: 360, objectFit: 'cover' }}/>
              ) : (
                /* Picked PDF with no image preview — show a document placeholder. */
                <div aria-label="Captured document" style={{
                  width: '100%', height: 200, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--ink-3)',
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2h8l4 4v14a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"/>
                    <path d="M14 2v4h4M8 13h8M8 17h6"/>
                  </svg>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>PDF document</div>
                </div>
              )}
              {/* Page-count badge for multi-page invoices */}
              {multiPage && (
                <div style={{
                  position: 'absolute', top: 10, right: 10,
                  background: 'rgba(0,0,0,0.65)', color: '#fff',
                  fontSize: 11.5, fontWeight: 600, borderRadius: 8, padding: '3px 8px',
                }}>{session.pageCount} pages</div>
              )}
              {/* Scan-line shimmer */}
              {phase !== 'error' && (
                <div aria-hidden="true" style={{
                  position: 'absolute', left: 0, right: 0, height: 3,
                  background: 'linear-gradient(90deg, transparent, var(--brand), transparent)',
                  animation: 'kanjo-scanline 1.6s ease-in-out infinite',
                }}/>
              )}
              <style>{`@keyframes kanjo-scanline {
                0%   { top: 0%; opacity: 0; }
                15%  { opacity: 1; }
                85%  { opacity: 1; }
                100% { top: 100%; opacity: 0; }
              }`}</style>
            </div>
          )}

          {phase !== 'error' ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
                {phase === 'uploading' ? 'Uploading…' : 'Reading the receipt…'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--on-dim)', maxWidth: 260, margin: '0 auto', lineHeight: 1.45 }}>
                Most receipts take a few seconds. Hang tight.
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', maxWidth: 280 }}>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: 'var(--danger)' }}>
                {errorCode === 'ZIP_REQUIRED' ? 'Add your ZIP code first' : "Couldn't read this one"}
              </div>
              <div style={{ fontSize: 13, color: 'var(--on-dim)', lineHeight: 1.45, marginBottom: 18 }}>
                {errorCode === 'ZIP_REQUIRED'
                  ? 'Kanjo groups prices by area. Set your restaurant ZIP code, then scan again.'
                  : (error || 'Try again with brighter light or a flatter receipt.')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {errorCode === 'ZIP_REQUIRED' ? (
                  <KLink to="/account" asButton style={{ width: '100%' }}>
                    <button className="btn primary lg" style={{ width: '100%' }}
                            onClick={() => scanReset()}>Set ZIP code</button>
                  </KLink>
                ) : (
                  <KLink to="/receipts/scan" asButton style={{ width: '100%' }}>
                    <button className="btn primary lg" style={{ width: '100%' }}
                            onClick={() => scanReset()}>Try again</button>
                  </KLink>
                )}
                <KLink to="/receipts" asButton style={{ width: '100%' }}>
                  <button className="btn ghost lg" style={{ width: '100%' }}
                          onClick={() => scanReset()}>Cancel</button>
                </KLink>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmLeave && (
        <ConfirmLeaveScan
          onStay={() => setConfirmLeave(false)}
          onLeave={leaveScan}
        />
      )}
    </KShell>
  );
}

// ── State 2: Confirm parsed line items (editable) ────────────
function ScanConfirm({ dark = false, persona = 'owner' }) {
  const nav = React.useContext(KanjoNav);

  // Pull OCR from the cross-screen handoff.
  const sourceOcr = (window.__kanjoScan && window.__kanjoScan.ocr) || null;

  // Local editable state seeded from OCR.
  const [draft, setDraft] = React.useState(() => buildDraft(sourceOcr));
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);
  // When a duplicate is detected at save time we hold it here to prompt the
  // user: discard the scan, or save it anyway (flagged as a duplicate).
  const [dupePrompt, setDupePrompt] = React.useState(null);
  // When the issue date falls outside the recent window (future, or older than
  // the current/previous week) we hold the classification here to confirm the
  // date before saving — it's usually an account/resale number misread as a date.
  const [datePrompt, setDatePrompt] = React.useState(null);
  // Same back-guard as ScanProcessing: edits here are unsaved, so confirm before
  // discarding and return to the receipts home rather than re-running OCR.
  const [confirmLeave, setConfirmLeave] = React.useState(false);
  const leaveScan = () => { scanReset(); nav.navigate('/receipts'); };

  // Units the operator has previously marked as sold-in-bulk (CS, SX, …). Loaded
  // once on mount, then applied to any matching line so catch-weight items come
  // in pre-marked and don't re-flag the "doesn't add up" warning every receipt.
  const [bulkUoms, setBulkUoms] = React.useState([]);
  React.useEffect(() => {
    let alive = true;
    window.kanjo?.ocr?.getBulkUoms?.().then((list) => {
      if (!alive || !Array.isArray(list) || !list.length) return;
      setBulkUoms(list);
      setDraft((d) => {
        let changed = false;
        const lines = d.lines.map((l) =>
          (!l.bulk && window.kanjo.ocr.isBulkUom(l.uom, list)) ? (changed = true, { ...l, bulk: true }) : l);
        return changed ? { ...d, lines } : d;
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, []); // eslint-disable-line

  React.useEffect(() => {
    if (!sourceOcr) {
      // No OCR loaded — back to scan.
      nav.navigate('/receipts/scan');
    }
  }, []); // eslint-disable-line

  if (!sourceOcr) return null;

  // Totals come from lib/ocr/invoiceTotals.js so the screen, the reconciler and
  // the saved record can never disagree about what the total is.
  const totals = window.kanjo?.ocr?.invoiceTotals?.(draft)
    || { subtotalCents: 0, taxCents: 0, freightCents: 0, otherCents: 0, discountCents: 0, totalCents: 0, printedTotalCents: null };
  const { subtotalCents, totalCents } = totals;
  // Arithmetic sanity check: do the lines add up to the invoice's printed total,
  // and does each line's qty × unit price match its printed line total? A
  // mismatch is the signature of a mis-read column (classically an item number
  // taken as a quantity). Recomputed every render so corrections clear it live.
  const recon = window.kanjo?.ocr?.reconcileInvoice?.(draft) || null;
  // Charges the SCAN added on top of the line items that no human has confirmed.
  // They are what let the items and the total disagree while everything still
  // foots — so they are surfaced until the operator edits or clears them.
  // Below UNCONFIRMED_FEE_THRESHOLD_CENTS, stay silent: a small tax/delivery
  // charge is the routine case (sales tax on a handful of taxable items on an
  // otherwise resale-exempt order, a small delivery fee) and flagging every one
  // trained operators to ignore the banner. Anything larger still surfaces —
  // that's the case actually worth a second look.
  const feeCents = (draft.taxCents || 0) + (draft.freightCents || 0);
  const unconfirmedFees = draft.feesUnconfirmed && Math.abs(feeCents) > UNCONFIRMED_FEE_THRESHOLD_CENTS
    ? {
      cents: feeCents,
      label: (draft.taxCents || 0) !== 0 && (draft.freightCents || 0) !== 0
        ? `${t('scan.tax', 'Tax')} + ${t('scan.deliveryFees', 'Delivery & fees')}`
        : (draft.taxCents || 0) !== 0 ? t('scan.tax', 'Tax') : t('scan.deliveryFees', 'Delivery & fees'),
    }
    : null;
  // Parser rule: a scanned receipt's issue date is RECENT — within the current or
  // previous week — and never in the future. A date outside that window is a
  // misread (classically a Costco-style account/resale number taken for a date,
  // pinning it years in the past), so we flag it and confirm before saving.
  const todayStr = window.kanjo?.ocr?.todayISO?.() || '';
  const dateISO = window.kanjo?.ocr?.parseISODate?.(draft.date) || '';
  const dateClass = window.kanjo?.ocr?.classifyIssueDate?.(draft.date, todayStr) || { status: 'unparseable', daysAgo: null };
  const dateFlagged = dateClass.status === 'future' || dateClass.status === 'ancient' || dateClass.status === 'stale';
  const currency = (window.__kanjoScan && window.__kanjoScan.currency) || 'USD';
  const locale = navigator.language || 'en-US';
  const fmt = (c) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format((c || 0) / 100);

  const updateLine = (idx, patch) => {
    setDraft((d) => {
      const next = d.lines.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...d, lines: next };
    });
  };
  const addLine = () => {
    setDraft((d) => ({
      ...d,
      lines: [...d.lines, { description: '', quantity: 1, unitPriceCents: 0, _new: true }],
    }));
  };
  // Toggle a line's "sold in bulk" flag and remember the association by unit:
  // marking a CS/SX line as bulk teaches Kanjo that the unit is catch-weight, so
  // it carries to other lines on this receipt sharing the unit and to future
  // scans. Toggling off forgets the unit again. Persistence is best-effort; the
  // on-screen state updates regardless.
  const toggleBulk = (idx) => {
    const target = draft.lines[idx];
    if (!target) return;
    const nextBulk = !target.bulk;
    const key = window.kanjo?.ocr?.normalizeUom?.(target.uom) || '';
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l, i) =>
        (i === idx || (key && window.kanjo.ocr.normalizeUom(l.uom) === key))
          ? { ...l, bulk: nextBulk } : l),
    }));
    if (key) {
      const fn = nextBulk ? window.kanjo.ocr.addBulkUom : window.kanjo.ocr.removeBulkUom;
      fn(key).then((updated) => setBulkUoms(updated || [])).catch((e) => console.warn('[Scan] bulk-unit memory failed', e));
    }
  };
  const removeLine = (idx) => {
    setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== idx) }));
  };

  // Persist the draft. `opts.replaceId` is set when the user chose to REPLACE a
  // detected duplicate: we save the new scan, then delete the old invoice it
  // supersedes (save-first so a delete failure can't lose both).
  const persist = async (opts = {}, draftOverride) => {
    setSaving(true); setSaveError(null);
    try {
      const ocr = draftToOcr(draftOverride || draft, totalCents, subtotalCents);
      const { invoiceId } = await window.kanjo.ocr.saveInvoiceFromOcr(ocr, opts);
      // Replace flow: the new scan is now canonical — remove the superseded one.
      if (opts.replaceId) {
        try { await window.kanjo.ocr.deleteInvoice(opts.replaceId); }
        catch (e) { console.warn('[Scan] replace: delete old invoice failed', e); }
      }
      // Link the cached scan blob (if any) to this invoice so the detail
      // screen can show the original. Best-effort.
      try {
        const rid = window.__kanjoScan?.receiptId;
        if (rid) await window.kanjo.ocr.linkReceiptImage(rid, invoiceId);
      } catch (e) { console.warn('[Scan] link image failed', e); }
      // Meter scanned pages — this also starts the 4-week free trial on the
      // first page (no-op if already metered/subscribed).
      try {
        const pages = (sourceOcr && (sourceOcr.billedPages || sourceOcr.pageCount)) || window.__kanjoScan?.pageCount || 1;
        await window.kanjo.scale?.plan?.recordPages?.(pages);
      } catch { /* metering is non-fatal */ }
      window.__kanjoScan.savedInvoice = { invoiceId, vendorName: ocr.vendorName, totalCents, dueDate: ocr.dueDate };
      // Drop the heavy blob now that the invoice is persisted.
      try { if (window.__kanjoScan.blobUrl) URL.revokeObjectURL(window.__kanjoScan.blobUrl); } catch {}
      try { if (window.__kanjoScan.previewUrl) URL.revokeObjectURL(window.__kanjoScan.previewUrl); } catch {}
      window.__kanjoScan.blob = null;
      window.__kanjoScan.blobUrl = null;
      window.__kanjoScan.previewUrl = null;
      nav.navigate('/receipts/scan/done');
    } catch (e) {
      console.warn('[Scan] save failed', e);
      setSaveError(e.message || 'Could not save invoice.');
      setSaving(false);
    }
  };

  // Duplicate gate, then persist. Split out so the date-confirmation step can
  // chain into it once the operator confirms an out-of-range date.
  // `draftOverride` lets the date-confirm "Set to today" path save with the
  // corrected date in the SAME tick — setDraft's update isn't visible to this
  // closure synchronously, so we thread the corrected draft through explicitly.
  const checkDuplicateThenPersist = async (draftOverride) => {
    setSaving(true); setSaveError(null);
    let dupe = null;
    try {
      const ocr = draftToOcr(draftOverride || draft, totalCents, subtotalCents);
      dupe = await window.kanjo.ocr.findDuplicate(ocr).catch(() => null);
    } catch { /* detection is best-effort */ }
    if (dupe) {
      // Same supplier + invoice #, or same supplier/date/amount: ask before saving.
      setSaving(false);
      setDupePrompt(dupe);
      return;
    }
    await persist({}, draftOverride);
  };

  const onSave = async () => {
    // Date gate first: an out-of-range issue date (future, or older than the
    // current/previous week) is usually a misread — confirm it before anything
    // else, since duplicate detection itself keys partly on the date.
    if (dateFlagged) { setDatePrompt(dateClass); return; }
    await checkDuplicateThenPersist();
  };

  return (
    <KShell dark={dark} tab="receipts" persona={persona} noNav={true}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <KHeader back title="Confirm" onBack={() => setConfirmLeave(true)}
          right={<svg width="20" height="20" viewBox="0 0 20 20" fill="var(--on-soft)" aria-hidden="true"><circle cx="4" cy="10" r="1.6"/><circle cx="10" cy="10" r="1.6"/><circle cx="16" cy="10" r="1.6"/></svg>}
        />

        {/* Vendor header card */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div aria-hidden="true" style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'var(--surface-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--sans)', fontSize: 16, fontWeight: 600,
              }}>{(draft.vendorName || '?')[0].toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <VendorPicker
                  compact
                  dark={dark}
                  initialName={sourceOcr?.vendorName || ''}
                  value={{ vendorId: draft.vendorId || null, name: draft.vendorName || '' }}
                  onChange={(v) => setDraft((d) => ({ ...d, vendorId: v.vendorId, vendorName: v.name }))}
                />
                <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {draft.invoiceNumber ? <span>Invoice {draft.invoiceNumber} ·</span> : null}
                  <input
                    type="date"
                    value={dateISO}
                    max={todayStr}
                    onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                    aria-label="Invoice date"
                    style={{
                      border: 'none', background: 'transparent', padding: 0, fontSize: 13,
                      fontFamily: 'inherit', colorScheme: dark ? 'dark' : 'light',
                      color: dateFlagged ? 'var(--warning,#c9941f)' : 'var(--ink-3)',
                      fontWeight: dateFlagged ? 600 : 400,
                    }}
                  />
                </div>
              </div>
              <div className="mono" style={{ fontSize: 20, fontWeight: 500 }}>{fmt(totalCents)}</div>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
          {/* Out-of-range date warning. A receipt is dated within the current or
              previous week and never in the future; anything else is usually a
              misread (an account/resale number taken for a date). Point the
              operator at the date field in the header above. Non-blocking here —
              save also confirms it via the date prompt. */}
          {draft.ocrDate && (
            <WarningBanner style={{ marginBottom: 12 }}>
              The scan read the date as <strong>{draft.ocrDate}</strong>, which isn't a
              date this receipt can have — usually a membership or account number
              taken for a date. It's been set to today (<strong>{draft.date}</strong>).
              Change it in the header above if the receipt is from another day.
              <button type="button"
                onClick={() => setDraft((d) => ({ ...d, date: d.ocrDate, ocrDate: null }))}
                style={{ display: 'block', marginTop: 6, background: 'transparent', border: 'none',
                  padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit',
                  textDecoration: 'underline' }}>
                Use {draft.ocrDate} anyway
              </button>
            </WarningBanner>
          )}
          {dateFlagged && !draft.ocrDate && (
            <WarningBanner style={{ marginBottom: 12 }}>
              {dateClass.status === 'future' ? (
                <>This invoice is dated <strong>{draft.date}</strong>, which is in the future — a receipt can't be. The date was likely misread; correct it in the header above before saving.</>
              ) : dateClass.status === 'ancient' ? (
                <>This invoice is dated <strong>{draft.date}</strong> — over {Math.floor(dateClass.daysAgo / 30)} months ago. A receipt that old is almost always a misread year, and it would land outside every report. Correct it in the header above, or confirm it's right when you save.</>
              ) : (
                <>This invoice is dated <strong>{draft.date}</strong> — {dateClass.daysAgo} days ago, more than a week back. Confirm it's right in the header above, or when you save.</>
              )}
            </WarningBanner>
          )}
          {/* Unconfirmed scanned charges. This is the case that used to slip
              through entirely: the items and the printed total disagree, an
              OCR-read tax/freight covers the difference exactly, so every
              arithmetic check passes and the operator is shown a total they
              cannot account for. Reported (above UNCONFIRMED_FEE_THRESHOLD_CENTS)
              until a human has looked at it — worded as an FYI, not an error:
              the scan succeeded, this is routine on plenty of invoices. */}
          {unconfirmedFees && (
            <WarningBanner style={{ marginBottom: 12 }}>
              {fillT(t('scan.unconfirmedCharges',
                'Includes {extra} {label} read from the scan — check Totals below if that looks wrong.'), {
                extra: fmt(unconfirmedFees.cents),
                label: unconfirmedFees.label,
              })}
            </WarningBanner>
          )}
          {/* Totals-don't-add-up warning. Fires only when the document stated a
              total that the parsed lines miss, or a line's qty × unit ≠ its line
              total — the tell-tale of a mis-read column. Non-blocking. */}
          {recon && (!recon.totalsReconcile || recon.anyLineSuspect) && (
            <WarningBanner style={{ marginBottom: 12 }}>
              {!recon.totalsReconcile ? (
                (draft.otherCents || 0) !== 0 ? (
                  // The operator set the total themselves. Not a scan fault —
                  // state the disagreement plainly and leave it standing.
                  <>You've set this invoice's total to <strong>{fmt(totals.totalCents)}</strong>, but the scanned document reads <strong>{fmt(recon.printedTotalCents)}</strong>. Your figure is what will be saved — clear “Other charges” in Totals below to go back to the scanned one.</>
                ) : recon.deltaCents < 0 ? (
                  // Captured lines fall SHORT of the printed total → items/fees missing.
                  <>These items add up to <strong>{fmt(recon.computedTotalCents)}</strong>, but the invoice total reads <strong>{fmt(recon.printedTotalCents)}</strong> — about <strong>{fmt(Math.abs(recon.deltaCents))}</strong> short. Some lines probably didn't scan (a second page, or rows the OCR skipped), or a delivery/fee line is missing. Add the missing lines so your COGS is complete.</>
                ) : (
                  // Captured lines OVERSHOOT the printed total → a misread (often an item # read as a qty).
                  <>These items add up to <strong>{fmt(recon.computedTotalCents)}</strong>, but the invoice total reads <strong>{fmt(recon.printedTotalCents)}</strong> — about <strong>{fmt(recon.deltaCents)}</strong> too high. A quantity or price was likely misread (often an item number read as a quantity) — check the highlighted lines before saving.</>
                )
              ) : (
                <>One or more lines don't add up — quantity × unit price doesn't match the line total. Check the highlighted lines.</>
              )}
            </WarningBanner>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, paddingLeft: 4, paddingRight: 4 }}>
            <div className="label">{draft.lines.length} item{draft.lines.length === 1 ? '' : 's'}</div>
            <button onClick={addLine} className="pill brand" style={{ border: 'none', cursor: 'pointer' }}>+ Add line</button>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {draft.lines.map((it, i) => (
              <LineRow key={i} idx={i} it={it} last={i === draft.lines.length - 1}
                       suspect={recon ? recon.lineOk[i] === false : false}
                       fmt={fmt} onChange={(p) => updateLine(i, p)} onRemove={() => removeLine(i)}
                       onToggleBulk={() => toggleBulk(i)} />
            ))}
            {draft.lines.length === 0 && (
              <div style={{ padding: '22px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                No items yet. Tap “Add line” to enter one.
              </div>
            )}
          </div>

          {/* Totals. Every part of the total is visible and editable here.
              This card is the fix for a real report: an invoice whose ten items
              summed to $95.30 displayed a $99.90 total, because tax and freight
              were folded into the header and rendered nowhere. Nothing was
              arithmetically wrong — which is exactly why no warning fired — but
              the operator had no account of the number they were saving, and no
              way to correct it. */}
          <TotalsCard
            totals={totals}
            fmt={fmt}
            dark={dark}
            onPatch={(patch) => setDraft((d) => ({ ...d, ...patch, feesUnconfirmed: false }))}
            onSetTotal={(cents) => setDraft((d) => ({
              ...d, ...(window.kanjo?.ocr?.totalOverridePatch?.(d, cents) || {}), feesUnconfirmed: false,
            }))}
          />

          {/* Auto-categorise note */}
          <div style={{
            marginTop: 14, padding: '12px 14px',
            background: 'var(--brand-bg)',
            borderRadius: 12,
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="var(--brand)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 1, flexShrink: 0 }}>
              <circle cx="9" cy="9" r="7.5"/>
              <path d="M9 5.5v4M9 12.5v.5"/>
            </svg>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45 }}>
              Saving as <span style={{ color: 'var(--ink)', fontWeight: 500 }}>COGS</span>
              {draft.dueDate ? <> · due <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{draft.dueDate}</span></> : null}
              .
            </div>
          </div>

          {saveError && (
            <ErrorBanner style={{ marginTop: 12 }}>{saveError}</ErrorBanner>
          )}
        </div>

        {/* CTA */}
        <div style={{ padding: '16px 20px 28px', borderTop: '1px solid var(--rule)', background: 'var(--bg)' }}>
          <button onClick={onSave} disabled={saving || draft.lines.length === 0}
                  className="btn primary lg"
                  style={{ width: '100%', opacity: (saving || draft.lines.length === 0) ? 0.6 : 1, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving…' : 'Save to books'}
          </button>
        </div>
      </div>

      {confirmLeave && (
        <ConfirmLeaveScan
          onStay={() => setConfirmLeave(false)}
          onLeave={leaveScan}
        />
      )}

      {datePrompt && (
        <ConfirmDate
          dateStr={draft.date}
          status={datePrompt.status}
          daysAgo={datePrompt.daysAgo}
          todayStr={todayStr}
          onToday={() => {
            // One tap to correct the overwhelmingly common case: a receipt
            // scanned today whose printed year the OCR misread (e.g. Costco
            // 2026→2025). Save with today's date in the same action.
            const corrected = { ...draft, date: todayStr };
            setDraft(corrected);
            setDatePrompt(null);
            checkDuplicateThenPersist(corrected);
          }}
          onKeep={() => { setDatePrompt(null); checkDuplicateThenPersist(); }}
          onFix={() => {
            // Prefill the header date field with today as a starting point —
            // the OCR'd date is what's wrong, so re-showing it just forces the
            // operator to scroll the picker back from years ago. They can still
            // adjust from today if the receipt really is from another day.
            setDraft((d) => ({ ...d, date: todayStr }));
            setDatePrompt(null);
          }}
        />
      )}

      {dupePrompt && (
        <ConfirmDuplicate
          match={dupePrompt.match}
          kind={dupePrompt.kind}
          scan={{ vendorName: draft.vendorName, invoiceNumber: draft.invoiceNumber, issuedDate: draft.date, totalCents, itemCount: draft.lines.length }}
          fmt={fmt}
          onReplace={() => { setDupePrompt(null); persist({ replaceId: dupePrompt.match.invoiceId }); }}
          onDiscard={() => { setDupePrompt(null); leaveScan(); }}
          onCancel={() => setDupePrompt(null)}
        />
      )}
    </KShell>
  );
}

// ── Date confirmation ────────────────────────────────────────
// Shown at save time when the issue date falls outside the recent window: in the
// future (impossible for a receipt) or well before this week (often a Costco-style
// account/resale number the OCR mistook for a date). The operator fixes the date,
// or confirms it's genuinely correct (a late-entered old receipt) and proceeds.
function ConfirmDate({ dateStr, status, daysAgo, todayStr, onToday, onKeep, onFix }) {
  return (
    <div role="dialog" aria-modal="true" aria-label="Check the date"
         onClick={onFix}
         style={{ position: 'absolute', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 420, background: 'var(--surface)', color: 'var(--ink)',
        borderTopLeftRadius: 20, borderTopRightRadius: 20,
        padding: '24px 22px calc(env(safe-area-inset-bottom, 0) + 22px)',
        boxShadow: '0 -12px 32px rgba(0,0,0,0.22)',
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Check the date</div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 22 }}>
          {status === 'future' ? (
            <>This receipt is dated <strong>{dateStr}</strong>, which is in the future — a receipt can't be. It was likely misread. Fix it, or keep it if it's really correct.</>
          ) : status === 'ancient' ? (
            <>This receipt is dated <strong>{dateStr}</strong> — <strong>over {Math.floor((daysAgo || 0) / 30)} months ago</strong>. A receipt that old is almost always a misread year, and it would sit outside every report you look at. Fix it, or keep it if this receipt really is from then.</>
          ) : (
            <>This receipt is dated <strong>{dateStr}</strong> — <strong>{daysAgo} days ago</strong>, more than a week back. Confirm that's right, or fix it.</>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {todayStr && onToday && (
            <button className="btn primary lg" onClick={onToday} style={{ width: '100%', cursor: 'pointer' }}>
              Set to today ({todayStr})
            </button>
          )}
          <button className="btn ghost lg" onClick={onFix} style={{ width: '100%', cursor: 'pointer' }}>
            Fix the date manually
          </button>
          <button className="btn ghost lg" onClick={onKeep} style={{ width: '100%', cursor: 'pointer' }}>
            Keep {dateStr} and save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Duplicate confirmation ───────────────────────────────────
// Shown when a just-scanned receipt matches one already on the books (same
// invoice #, or same date + amount). The two are laid out side by side so the
// owner can SELECT which receipt survives: keep the fresh scan (replacing the
// saved one), keep the saved one (discarding the scan), or go back to keep
// editing.
function ConfirmDuplicate({ match, kind, scan, fmt, onReplace, onDiscard, onCancel }) {
  // One receipt's summary as a labelled card. `tone` tints the active choice.
  const ReceiptCard = ({ label, vendorName, invoiceNumber, issuedDate, totalCents, itemCount }) => (
    <div style={{ flex: 1, minWidth: 0, padding: '12px 14px', borderRadius: 12, background: 'var(--surface-3)' }}>
      <div className="label" style={{ fontSize: 10.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {vendorName || 'Unknown vendor'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
        {invoiceNumber ? `#${invoiceNumber} · ` : ''}{issuedDate || '—'}
      </div>
      <div className="mono" style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{fmt(totalCents || 0)}</div>
      {itemCount != null && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{itemCount} item{itemCount === 1 ? '' : 's'}</div>
      )}
    </div>
  );
  return (
    <div role="dialog" aria-modal="true" aria-label="Possible duplicate"
         onClick={onCancel}
         style={{ position: 'absolute', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 420, background: 'var(--surface)', color: 'var(--ink)',
        borderTopLeftRadius: 20, borderTopRightRadius: 20,
        padding: '24px 22px calc(env(safe-area-inset-bottom, 0) + 22px)',
        boxShadow: '0 -12px 32px rgba(0,0,0,0.22)',
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Possible duplicate</div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 16 }}>
          This looks like a receipt you already saved
          {kind === 'exact' ? ' (same invoice number)' : ' (same date and amount)'}. Which one should
          Kanjo keep?
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <ReceiptCard label="Already saved" vendorName={match.vendorName} invoiceNumber={match.invoiceNumber}
                       issuedDate={match.issuedDate} totalCents={match.totalCents} />
          <ReceiptCard label="New scan" vendorName={scan?.vendorName} invoiceNumber={scan?.invoiceNumber}
                       issuedDate={scan?.issuedDate} totalCents={scan?.totalCents} itemCount={scan?.itemCount} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn primary lg" onClick={onReplace} style={{ width: '100%', cursor: 'pointer' }}>
            Keep the new scan
          </button>
          <button className="btn ghost lg" onClick={onDiscard} style={{ width: '100%', cursor: 'pointer' }}>
            Keep the saved one
          </button>
          <button className="btn ghost lg" onClick={onCancel} style={{ width: '100%', cursor: 'pointer' }}>
            Keep editing
          </button>
        </div>
      </div>
    </div>
  );
}

// A numeric input that's actually pleasant to edit on a keyboard. The old
// approach pinned `value` to a re-formatted string (toFixed(2)), so every
// keystroke reformatted the text under the caret — you couldn't click into
// "0.45" and type without the cursor jumping. Here:
//   • focusing CLEARS the field (the prior value stays as a greyed placeholder),
//     so the first keystroke replaces it — no manual cursor placement;
//   • while focused we show your raw keystrokes verbatim (no live reformatting
//     fighting the caret);
//   • leaving without typing keeps the old value; typing a number commits it;
//   • the formatted value returns on blur.
function NumField({ valueText, hint, onCommit, ariaLabel, bold = false }) {
  const [draft, setDraft] = React.useState(null); // null ⇒ not being edited
  const editing = draft !== null;
  return (
    <input
      inputMode="decimal"
      aria-label={ariaLabel}
      value={editing ? draft : valueText}
      placeholder={editing ? (valueText || hint) : hint}
      onFocus={() => setDraft('')}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const n = Number(raw);
        // Only commit a real number; empty/partial input ("", ".") leaves the
        // stored value untouched so an accidental focus never zeroes a price.
        if (raw.trim() !== '' && Number.isFinite(n)) onCommit(n);
      }}
      onBlur={() => setDraft(null)}
      style={{
        width: '100%', border: '1px solid var(--rule-2)', borderRadius: 8, padding: '6px 8px',
        fontSize: 14, color: 'var(--ink)', background: 'var(--surface)',
        fontFamily: 'var(--mono)', textAlign: 'right', fontWeight: bold ? 500 : 400,
      }}
    />
  );
}

function LineRow({ idx, it, last, suspect = false, fmt, onChange, onRemove, onToggleBulk }) {
  const [editing, setEditing] = React.useState(!!it._new);
  const ext = lineExtCents(it);
  const flag = suspect && !editing; // highlight a line whose numbers don't add up
  const bulkUnit = window.kanjo?.ocr?.normalizeUom?.(it.uom) || '';
  const discountCents = it.discountCents || 0;
  // The gap between qty × unit and the printed total. A POSITIVE gap that isn't
  // already explained by a recorded discount is almost always a vendor discount
  // (commonly printed in parens on the next line) — we offer to record it with
  // one tap so the after-discount total is trusted and the line reconciles.
  const grossCents = Math.round((Number(it.quantity) || 0) * (Number(it.unitPriceCents) || 0));
  const impliedDiscountCents = it.extPriceCents != null ? grossCents - it.extPriceCents - discountCents : 0;
  const inputStyle = {
    width: '100%', border: '1px solid var(--rule-2)', borderRadius: 8, padding: '6px 8px',
    fontSize: 14, color: 'var(--ink)', background: 'var(--surface)', fontFamily: 'var(--sans)',
  };
  return (
    <div style={{
      padding: '14px 16px',
      borderBottom: last ? 'none' : '1px solid var(--rule)',
      borderLeft: flag ? '3px solid var(--warning,#c9941f)' : '3px solid transparent',
      background: flag ? 'var(--warning-soft,#fff4e0)' : 'transparent',
      display: 'grid', gridTemplateColumns: editing ? '1fr' : '1fr auto', alignItems: 'center', gap: 14,
    }}>
      {editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
          <input value={it.description || ''} onChange={(e) => onChange({ description: e.target.value })}
                 aria-label={`Item ${idx + 1} description`} placeholder="Description" style={inputStyle}/>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {/* Editing qty or unit price clears any OCR-captured extended price
                so the total recomputes; editing the total field overrides it. */}
            <NumField ariaLabel="Quantity" hint="Qty"
                      valueText={it.quantity != null ? String(it.quantity) : ''}
                      onCommit={(n) => onChange({ quantity: n, extPriceCents: null })}/>
            <NumField ariaLabel="Unit price" hint="0.00"
                      valueText={((it.unitPriceCents || 0) / 100).toFixed(2)}
                      onCommit={(n) => onChange({ unitPriceCents: Math.round(n * 100), extPriceCents: null })}/>
            <NumField ariaLabel="Line total" hint="0.00" bold
                      valueText={(ext / 100).toFixed(2)}
                      onCommit={(n) => onChange({ extPriceCents: Math.round(n * 100) })}/>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: -2 }}>
            <div className="label" style={{ textAlign: 'right', fontSize: 10 }}>Qty</div>
            <div className="label" style={{ textAlign: 'right', fontSize: 10 }}>Unit price</div>
            <div className="label" style={{ textAlign: 'right', fontSize: 10 }}>Line total</div>
          </div>
          {/* Discount: when qty × unit overshoots the printed total, the gap is
              almost always a vendor discount (often printed in parens on the
              next line). Recording it makes the after-discount total trusted and
              clears the flag — and captures the discount for COGS. */}
          {discountCents > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, fontSize: 13, color: 'var(--ink-2)' }}>
              <span>Discount applied: <strong>−{fmt(discountCents)}</strong></span>
              <button onClick={() => onChange({ discountCents: 0 })}
                      style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--danger)', fontSize: 12.5, cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          ) : (impliedDiscountCents > 2 && !it.bulk) ? (
            <button onClick={() => onChange({ discountCents: impliedDiscountCents })}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 2,
                             background: 'transparent', border: 'none', padding: '4px 0',
                             cursor: 'pointer', color: 'var(--brand,#2f6f4f)', fontSize: 13, textAlign: 'left' }}>
              <span aria-hidden="true" style={{ fontWeight: 700, marginTop: 1 }}>%</span>
              <span>
                Record −{fmt(impliedDiscountCents)} discount
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1 }}>
                  Confirms the after-discount total {fmt(it.extPriceCents)} is correct
                </span>
              </span>
            </button>
          ) : null}
          {/* Catch-weight items (a sack/case billed by actual weight) won't have
              qty × unit = total. Marking the line as bulk trusts the printed
              total and skips the "doesn't add up" check — and remembers the unit
              (CS, SX, …) so future receipts inherit it. */}
          <button onClick={() => (onToggleBulk ? onToggleBulk() : onChange({ bulk: !it.bulk }))} aria-pressed={!!it.bulk}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 2,
                    background: 'transparent', border: 'none', padding: '4px 0',
                    cursor: 'pointer', color: 'var(--ink-2)', fontSize: 13, textAlign: 'left',
                  }}>
            <span aria-hidden="true" style={{
              width: 18, height: 18, borderRadius: 5, flex: '0 0 auto', marginTop: 1,
              border: `1.5px solid ${it.bulk ? 'var(--brand,#2f6f4f)' : 'var(--rule-2)'}`,
              background: it.bulk ? 'var(--brand,#2f6f4f)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {it.bulk && (
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.2l2.3 2.3 4.7-5"/></svg>
              )}
            </span>
            <span>
              Sold in bulk / by weight — skip the qty × price check
              {bulkUnit && (
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1 }}>
                  {it.bulk ? `Remembered for all “${bulkUnit}” lines` : `Also remembers “${bulkUnit}” for future receipts`}
                </span>
              )}
            </span>
          </button>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={() => setEditing(false)} className="btn ghost"
                    style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}>Done</button>
            <button onClick={onRemove} aria-label={`Remove item ${idx + 1}`}
                    style={{ marginLeft: 'auto', background: 'transparent', border: 'none',
                             color: 'var(--danger)', fontSize: 13, cursor: 'pointer' }}>Remove</button>
          </div>
        </div>
      ) : (
        <>
          <button onClick={() => setEditing(true)}
                  aria-label={`Edit ${it.description || 'line'}`}
                  style={{ display: 'block', textAlign: 'left', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minWidth: 0, color: 'var(--ink)' }}>
            <div style={{ fontSize: 15, fontWeight: 500 }}>{it.description || <span style={{ color: 'var(--ink-3)' }}>Untitled item</span>}</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
              {(it.quantity ?? 0)} × {it.packSize || it.uom || 'each'}
              {it.bulk && (
                <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}> · sold in bulk</span>
              )}
              {discountCents > 0 && (
                <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}> · −{fmt(discountCents)} disc</span>
              )}
            </div>
            {flag && (
              <div style={{ fontSize: 11.5, color: 'var(--warning,#c9941f)', fontWeight: 600, marginTop: 3 }}>
                ⚠ Doesn't add up — tap to fix, record a discount, or mark bulk
              </div>
            )}
          </button>
          <button onClick={() => setEditing(true)} style={{
            background: 'transparent', border: 'none', textAlign: 'right', padding: 0, cursor: 'pointer',
          }} aria-label={`Edit price for ${it.description || 'line'}`}>
            {/* Money colour = meaning: a cost coming DOWN is green. The
                aggregated savings line is the one negative amount here, so it
                reads as the saving it is rather than another charge. */}
            <div className="mono" style={{ fontSize: 15, fontWeight: 500,
              color: ext < 0 ? 'var(--positive)' : 'var(--ink)' }}>{fmt(ext)}</div>
            {!it.isDiscount && (
              <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{fmt(it.unitPriceCents || 0)} ea</div>
            )}
          </button>
        </>
      )}
    </div>
  );
}

// One definition of a line total, shared with the reconciler and the totals
// block — see lib/ocr/invoiceTotals.js.
function lineExtCents(it) {
  const f = window.kanjo?.ocr?.lineExtendedCents;
  if (f) return f(it);
  if (!it) return 0;
  if (it.extPriceCents != null) return it.extPriceCents;
  return Math.max(0, Math.round((Number(it.quantity) || 0) * (Number(it.unitPriceCents) || 0)) - (it.discountCents || 0));
}

// Fill {name} placeholders in a translated string with React nodes, bolding
// each substituted value. Keeps a sentence one translatable unit instead of
// splintering it into fragments that no translator can reorder.
function fillT(template, values) {
  return String(template).split(/(\{[a-zA-Z]+\})/g).map((part, i) => {
    const m = /^\{([a-zA-Z]+)\}$/.exec(part);
    return m && m[1] in values ? <strong key={i}>{values[m[1]]}</strong> : part;
  });
}

// A single editable money row in the totals card. Empty means zero; the field
// accepts a negative amount because a credit is a real thing on an invoice.
function TotalsRow({ label, cents, onChange, dark, fmt, strong, hint, readOnly }) {
  const [text, setText] = React.useState(null); // non-null while being edited
  const shown = text != null ? text : (cents ? (cents / 100).toFixed(2) : '');
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 12, padding: '9px 16px', borderTop: '1px solid var(--rule)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: strong ? 14.5 : 13.5, fontWeight: strong ? 600 : 400,
          color: strong ? 'var(--ink)' : 'var(--ink-2)' }}>{label}</div>
        {hint && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{hint}</div>}
      </div>
      {readOnly ? (
        <span className="mono" style={{ fontSize: strong ? 16 : 14, fontWeight: strong ? 700 : 500 }}>{fmt(cents)}</span>
      ) : (
        <input
          inputMode="decimal"
          aria-label={label}
          value={shown}
          placeholder="0.00"
          onFocus={() => setText(cents ? (cents / 100).toFixed(2) : '')}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const v = Number(String(text).replace(/[^0-9.-]/g, ''));
            onChange(Number.isFinite(v) ? Math.round(v * 100) : 0);
            setText(null);
          }}
          style={{ width: 104, padding: '5px 8px', borderRadius: 8,
            border: '1px solid var(--rule)', background: 'var(--surface-2)',
            fontFamily: 'var(--mono)', fontSize: strong ? 16 : 14,
            fontWeight: strong ? 700 : 500, textAlign: 'right', color: 'var(--ink)',
            colorScheme: dark ? 'dark' : 'light' }}
        />
      )}
    </div>
  );
}

// Rows for tax / delivery / discount appear once they carry a value, and are
// otherwise reachable through "Add a charge" — an empty invoice shouldn't open
// with four zero fields, but a non-zero one must never hide a charge.
function TotalsCard({ totals, fmt, dark, onPatch, onSetTotal }) {
  const v = totals; // NOT `t` — that is the i18n lookup
  const [openAll, setOpenAll] = React.useState(false);
  const show = openAll || v.taxCents !== 0 || v.freightCents !== 0
    || v.otherCents !== 0 || v.discountCents !== 0;
  const printedDiffers = v.printedTotalCents != null && v.printedTotalCents !== v.totalCents;
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 14 }}>
      <div style={{ padding: '12px 16px 4px' }}>
        <div className="label">{t('scan.totals', 'Totals')}</div>
      </div>
      <TotalsRow label={t('scan.itemsSubtotal', 'Items subtotal')} cents={v.subtotalCents} fmt={fmt} readOnly/>
      {show && (
        <>
          <TotalsRow label={t('scan.tax', 'Tax')} cents={v.taxCents} fmt={fmt} dark={dark}
            onChange={(c) => onPatch({ taxCents: c })}/>
          <TotalsRow label={t('scan.deliveryFees', 'Delivery & fees')} cents={v.freightCents} fmt={fmt} dark={dark}
            onChange={(c) => onPatch({ freightCents: c })}/>
          <TotalsRow label={t('scan.discount', 'Discount')} cents={v.discountCents} fmt={fmt} dark={dark}
            onChange={(c) => onPatch({ discountCents: c })}/>
          {v.otherCents !== 0 && (
            <TotalsRow label={t('scan.otherCharges', 'Other charges')} cents={v.otherCents} fmt={fmt} dark={dark}
              hint={t('scan.otherHint', 'Your adjustment to the total')}
              onChange={(c) => onPatch({ otherCents: c })}/>
          )}
        </>
      )}
      {!show && (
        <div style={{ padding: '8px 16px 10px', borderTop: '1px solid var(--rule)' }}>
          <button onClick={() => setOpenAll(true)} className="pill"
            style={{ border: '1px solid var(--rule)', background: 'transparent', color: 'var(--ink-2)', cursor: 'pointer' }}>
            {t('scan.addCharge', '+ Tax, delivery or discount')}
          </button>
        </div>
      )}
      <TotalsRow label={t('scan.total', 'Total')} cents={v.totalCents} fmt={fmt} dark={dark} strong
        hint={printedDiffers
          ? t('scan.invoiceReads', 'Invoice reads {v}').replace('{v}', fmt(v.printedTotalCents))
          : null}
        onChange={onSetTotal}/>
    </div>
  );
}

function buildDraft(ocr) {
  if (!ocr) return { vendorName: '', invoiceNumber: '', date: '', dueDate: '', taxCents: 0, freightCents: 0, discountCents: 0, otherCents: 0, lines: [] };
  // See ocr/dates.resolveIssueDate: an out-of-range scanned date is replaced by
  // today and handed back, so the banner can show what was read and offer it.
  const issue = window.kanjo?.ocr?.resolveIssueDate?.(ocr.date) || { date: ocr.date || '', scannedDate: null };
  return {
    vendorName: ocr.vendorName || '',
    // Filled by the vendor picker once it resolves the scanned name against the
    // suppliers on file. Until then the name alone is what the saver would use.
    vendorId: null,
    invoiceNumber: ocr.invoiceNumber || '',
    poNumber: ocr.poNumber || '',
    // A misread date is NOT carried into the draft. Warehouse-club receipts
    // print a membership/account number that the extractor sometimes takes for
    // a date, pinning the receipt years in the past (a Costco scan on
    // 2026-08-05 came back 2023-08-05 — right day, junk year). Flagging that
    // and leaving it in the field just makes the operator retype it. A receipt
    // is scanned when it is bought, so TODAY is the right default; the scanned
    // value is kept in `ocrDate` so the banner can show it and offer it back.
    date: issue.date || ocr.date || '',
    ocrDate: issue.scannedDate,
    dueDate: ocr.dueDate || '',
    terms: ocr.terms || '',
    taxCents: ocr.tax != null ? Math.round(ocr.tax * 100) : 0,
    freightCents: ocr.freight != null ? Math.round(ocr.freight * 100) : 0,
    discountCents: 0,
    otherCents: 0, // operator's own adjustment to the total; see the Totals card
    // Tax/freight come straight from the extractor with nothing corroborating
    // them, and they are the one thing that can make the items disagree with
    // the total WITHOUT tripping any arithmetic check — the total still foots,
    // so the reconciler is satisfied while the operator is looking at a charge
    // their supplier may never have made. Flag them until a human has looked.
    feesUnconfirmed: (ocr.tax != null && ocr.tax !== 0) || (ocr.freight != null && ocr.freight !== 0),
    // Keep the invoice's PRINTED total (when stated) so we can flag a parse whose
    // lines don't add up to it — e.g. an item number mis-read as a quantity.
    printedTotalCents: ocr.total != null ? Math.round(ocr.total * 100) : null,
    lines: (ocr.lineItems || []).map((li) => ({
      description: li.description || '',
      sku: li.sku,
      quantity: Number(li.quantity) || 0,
      uom: li.uom,
      packSize: li.packSize,
      unitPriceCents: li.unitPrice != null ? Math.round(li.unitPrice * 100) : 0,
      extPriceCents: li.extendedPrice != null ? Math.round(li.extendedPrice * 100) : null,
      discountCents: li.discount != null ? Math.round(li.discount * 100) : 0, // per-line discount (printed in parens)
      taxable: li.taxable,
      bulk: !!li.bulk, // sold by weight — exempt from the qty × price check
      isDiscount: !!li.isDiscount, // the aggregated money-off line
      confidence: li.confidence,
    })),
  };
}

function draftToOcr(d, totalCents, subtotalCents) {
  return {
    // Set once the operator has a supplier selected in the picker. It's the
    // authoritative choice — the saver won't re-resolve the name around it.
    vendorId: d.vendorId || undefined,
    vendorName: d.vendorName,
    invoiceNumber: d.invoiceNumber,
    poNumber: d.poNumber,
    date: d.date,
    dueDate: d.dueDate,
    terms: d.terms,
    subtotal: subtotalCents / 100,
    tax: (d.taxCents || 0) / 100,
    freight: (d.freightCents || 0) / 100,
    other: (d.otherCents || 0) / 100,
    total: totalCents / 100,
    lineItems: d.lines.map((l) => ({
      description: l.description,
      sku: l.sku,
      quantity: Number(l.quantity) || 0,
      uom: l.uom,
      packSize: l.packSize,
      unitPrice: (l.unitPriceCents || 0) / 100,
      extendedPrice: lineExtCents(l) / 100,
      discount: (l.discountCents || 0) / 100,
      taxable: l.taxable,
      bulk: !!l.bulk,
      isDiscount: !!l.isDiscount, // stays out of the item catalogue and price book
      confidence: l.confidence,
    })),
  };
}

// ── State 3: Saved + insight ────────────────────────────────
function ScanDone({ dark = false, persona = 'owner' }) {
  const saved = (window.__kanjoScan && window.__kanjoScan.savedInvoice) || null;
  const locale = navigator.language || 'en-US';
  const currency = 'USD';
  const fmt = (c) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format((c || 0) / 100);

  return (
    <KShell dark={dark} tab="receipts" persona={persona} noNav={true}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <KHeader back={false} right={
          <KLink to="/today" asButton>
            <div style={{ fontSize: 14, color: 'var(--on-soft)', fontWeight: 500 }}>Done</div>
          </KLink>
        }/>

        {/* Hero confirmation */}
        <div style={{ padding: '32px 28px 24px', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28,
            background: 'var(--positive-soft)', color: 'var(--positive)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 18,
          }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 11l4 4 8-9"/>
            </svg>
          </div>
          <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 8 }}>Saved</div>
          <div style={{ fontSize: 15, color: 'var(--on-soft)', lineHeight: 1.4, maxWidth: 280, margin: '0 auto' }}>
            {saved ? (
              <>
                {fmt(saved.totalCents)} added to this week's COGS.<br/>
                {saved.vendorName ? `Due to ${saved.vendorName}${saved.dueDate ? ` on ${saved.dueDate}` : ''}.` : null}
              </>
            ) : (
              <>Invoice saved to your books.</>
            )}
          </div>
        </div>

        <div style={{ flex: 1 }}/>

        {/* Actions */}
        <div style={{ padding: '12px 20px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <KLink to="/receipts/scan" asButton style={{ width: '100%' }}>
            <button className="btn primary lg" style={{ width: '100%' }}
                    onClick={() => scanReset()}>Scan another</button>
          </KLink>
          <KLink to="/today" asButton style={{ width: '100%' }}>
            <button className="btn ghost lg" style={{ width: '100%' }}
                    onClick={() => scanReset()}>Back to today</button>
          </KLink>
        </div>
      </div>
    </KShell>
  );
}

// ── Vendor picker ────────────────────────────────────────────
// Typing a supplier name free-hand is how you end up with "City Produce" and
// "City Produe" as two vendors, splitting a supplier's spend and price history
// in half. So the default is CHOOSING from the suppliers already on the books
// (most recently used first — usually the one you're holding an invoice from),
// and adding a new one is a deliberate second step.
//
// Even then a new name can be a misspelling of somebody already on file, so we
// run the same fuzzy matcher the OCR save path uses and offer the near-match
// back: "Did you mean City Produce?" — one tap to use it instead.
//
// Reports { vendorId } when an existing supplier is chosen and { name } when a
// genuinely new one is typed; the saver treats a vendorId as final.
function VendorPicker({ value, onChange, dark = false, initialName = '', compact = false }) {
  // Kept inside the component: in dev the in-browser Babel lowers a top-level
  // const to a global `var`, and this file already owns enough window names.
  const NEW_VENDOR = '__new__';
  const [vendors, setVendors] = React.useState(null);   // null = still loading
  const [mode, setMode] = React.useState(null);         // vendorId | NEW_VENDOR
  // Set when we opened on a supplier whose name differs from what the scan read
  // — the operator should see WHY the card says something the receipt doesn't.
  const [matchedFrom, setMatchedFrom] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      let vs = [];
      try {
        vs = await window.kanjo.procurement.vendors.listForPicker();
      } catch (e) {
        console.warn('[VendorPicker] load failed', e);
      }
      if (cancelled) return;
      setVendors(vs);

      // Seed from a name we were handed (the scan's reading of the vendor).
      // Same matcher the save path uses, so the picker opens on exactly the
      // supplier the save would have chosen — just visible, and overridable.
      const seed = (initialName || '').trim();
      const hit = seed && vs.length
        ? window.kanjo?.procurement?.vendorMatch?.bestVendorMatch?.(seed, vs)
        : null;
      if (hit) {
        setMode(hit.vendorId);
        setMatchedFrom(hit.name.toLowerCase() === seed.toLowerCase() ? null : seed);
        onChange({ vendorId: hit.vendorId, name: hit.name });
        return;
      }
      // A name with no match is a new supplier — carry the reading over so it
      // doesn't have to be retyped. No suppliers on file at all (first receipt
      // ever) lands here too, rather than on an empty picker.
      if (seed || !vs.length) {
        setMode(NEW_VENDOR);
        onChange({ vendorId: null, name: seed });
        return;
      }
      setMode('');
    })();
    return () => { cancelled = true; };
  }, []);

  // A typed name that looks like a supplier already on file. Deliberately not
  // auto-corrected — the operator may really have two similarly-named vendors.
  const nearMatch = React.useMemo(() => {
    if (mode !== NEW_VENDOR || !value.name?.trim() || !vendors?.length) return null;
    const hit = window.kanjo?.procurement?.vendorMatch?.bestVendorMatch?.(value.name.trim(), vendors);
    return hit && hit.name.toLowerCase() !== value.name.trim().toLowerCase() ? hit : null;
  }, [mode, value.name, vendors]);

  // `compact` blends the control into the confirm screen's vendor header card,
  // where the supplier is the card's title rather than a labelled form field.
  const field = compact
    ? { width: '100%', padding: 0, border: 'none', background: 'transparent',
        color: 'var(--ink)', fontSize: 16, fontWeight: 600, boxSizing: 'border-box' }
    : { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--rule)',
        background: 'var(--surface-2)', color: 'var(--ink)', fontSize: 15, boxSizing: 'border-box' };

  if (vendors == null) return <div style={{ ...field, color: 'var(--ink-3)' }}>Loading suppliers…</div>;

  return (
    <>
      {vendors.length > 0 && (
        <select
          value={mode}
          onChange={(e) => {
            const v = e.target.value;
            setMode(v);
            setMatchedFrom(null);
            onChange(v === NEW_VENDOR ? { vendorId: null, name: '' }
              : { vendorId: v || null, name: vendors.find((x) => x.vendorId === v)?.name || '' });
          }}
          aria-label="Supplier"
          style={{ ...field, colorScheme: dark ? 'dark' : 'light' }}
        >
          <option value="">Choose a supplier…</option>
          {vendors.map((v) => (
            <option key={v.vendorId} value={v.vendorId}>
              {/* Browsing EXISTING suppliers is display: the fake goes on screen
                  while onChange below still hands the caller the stored name, so
                  what gets saved on this invoice is untouched. */}
              {v.invoiceCount ? `${vendorName(v.name)} (${v.invoiceCount})` : vendorName(v.name)}
            </option>
          ))}
          <option value={NEW_VENDOR}>+ New supplier…</option>
        </select>
      )}

      {matchedFrom && (
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.4 }}>
          Matched from “{matchedFrom}” on the receipt.
        </div>
      )}

      {mode === NEW_VENDOR && (
        <>
          <input
            value={value.name || ''}
            onChange={(e) => onChange({ vendorId: null, name: e.target.value })}
            placeholder="New supplier name"
            aria-label="New supplier name"
            autoFocus={vendors.length > 0 && !initialName}
            style={{ ...field, marginTop: vendors.length ? 8 : 0 }}
          />
          {nearMatch && (
            <button
              type="button"
              onClick={() => { setMode(nearMatch.vendorId); onChange({ vendorId: nearMatch.vendorId, name: nearMatch.name }); }}
              style={{ marginTop: 8, width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 10,
                border: '1px solid var(--warning)', background: 'transparent', color: 'var(--warning)',
                fontSize: 13, lineHeight: 1.4, cursor: 'pointer' }}
            >
              Did you mean <strong>{vendorName(nearMatch.name)}</strong>? Tap to use it instead of creating a second supplier.
            </button>
          )}
        </>
      )}
    </>
  );
}

// ── Manual entry: no receipt to scan ─────────────────────────
// Reached from the (+) sheet's "Enter manually" option — for an invoice that
// was never scanned and can't be found (lost paper, missed delivery). The
// operator supplies only what they actually know: vendor, date, and the
// amount split into base / tax / delivery — no line items, since there's
// nothing to read them from.
function ManualEntry({ dark = false, persona = 'owner' }) {
  const nav = React.useContext(KanjoNav);
  const fmt = (c) => new Intl.NumberFormat(navigator.language || 'en-US', { style: 'currency', currency: 'USD' }).format((c || 0) / 100);
  const [vendor, setVendor] = React.useState({ vendorId: null, name: '' });
  const vendorName = vendor.name;
  const [date, setDate] = React.useState(() => window.kanjo?.ocr?.todayISO?.() || '');
  const [amountCents, setAmountCents] = React.useState(0);
  const [taxCents, setTaxCents] = React.useState(0);
  const [freightCents, setFreightCents] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);
  const [dupePrompt, setDupePrompt] = React.useState(null);
  const [datePrompt, setDatePrompt] = React.useState(null);
  const [viewer, setViewer] = React.useState(false);
  React.useEffect(() => {
    window.kanjo?.scale?.account?.currentAccount?.().then((a) => setViewer(a?.role === 'viewer')).catch(() => {});
  }, []);
  const totalCents = amountCents + taxCents + freightCents;
  const canSave = vendorName.trim() && date && totalCents > 0 && !saving;
  // Typing a date by hand has the same failure modes as reading one off a
  // receipt — a wrong year lands the invoice outside every report, silently.
  // Same check the scan path uses, on whatever date is in the field.
  const todayStr = window.kanjo?.ocr?.todayISO?.() || '';
  const dateClass = window.kanjo?.ocr?.classifyIssueDate?.(date, todayStr) || { status: 'unparseable', daysAgo: null };
  const dateFlagged = dateClass.status === 'future' || dateClass.status === 'ancient' || dateClass.status === 'stale';

  // `dateOverride` threads a just-corrected date through in the SAME tick —
  // setDate's update isn't visible to these closures synchronously, so the
  // "Set to today" path would otherwise save the date it was correcting.
  const buildOcr = (dateOverride) => ({
    vendorId: vendor.vendorId || undefined,
    vendorName: vendorName.trim(),
    date: dateOverride || date,
    subtotal: amountCents / 100,
    tax: taxCents / 100,
    freight: freightCents / 100,
    total: totalCents / 100,
    lineItems: [],
  });

  const persist = async (opts = {}, dateOverride) => {
    setSaving(true); setSaveError(null);
    try {
      const ocr = buildOcr(dateOverride);
      const { invoiceId } = await window.kanjo.ocr.saveInvoiceFromOcr(ocr, opts);
      window.__kanjoScan.savedInvoice = { invoiceId, vendorName: ocr.vendorName, totalCents, dueDate: null };
      nav.navigate('/receipts/scan/done');
    } catch (e) {
      console.warn('[ManualEntry] save failed', e);
      setSaveError(e.message || 'Could not save invoice.');
      setSaving(false);
    }
  };

  const checkDuplicateThenPersist = async (dateOverride) => {
    setSaving(true); setSaveError(null);
    let dupe = null;
    try { dupe = await window.kanjo.ocr.findDuplicate(buildOcr(dateOverride)).catch(() => null); } catch { /* best-effort */ }
    if (dupe) { setSaving(false); setDupePrompt(dupe); return; }
    await persist({}, dateOverride);
  };

  const onSave = async () => {
    if (!canSave) return;
    // Date gate first — duplicate detection keys partly on the date, so there's
    // no point matching against a date the operator is about to change.
    if (dateFlagged) { setDatePrompt(dateClass); return; }
    await checkDuplicateThenPersist();
  };

  if (viewer) {
    return (
      <KShell dark={dark} tab="receipts" persona={persona} noNav={true}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
          <KHeader back title="Enter invoice" onBack={() => nav.navigate('/receipts')} />
          <div style={{ flex: 1, padding: '8px 28px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 600 }}>Read-only access</div>
            <div style={{ fontSize: 14, color: 'var(--on-soft)', lineHeight: 1.5, maxWidth: 300 }}>
              You're viewing the account owner's books. Adding invoices is available to the account owner.
            </div>
            <KLink to="/receipts" asButton style={{ width: '100%', maxWidth: 280 }}>
              <button className="btn primary lg" style={{ width: '100%' }}>Back to receipts</button>
            </KLink>
          </div>
        </div>
      </KShell>
    );
  }

  return (
    <KShell dark={dark} tab="receipts" persona={persona} noNav={true}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <KHeader back title="Enter invoice" onBack={() => nav.navigate('/receipts')} />
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, padding: '0 0 14px' }}>
            No receipt to scan? Enter the amount, tax and delivery fee yourself — it'll count toward this week's COGS the same way.
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div className="label" style={{ marginBottom: 6 }}>Supplier</div>
            <VendorPicker value={vendor} onChange={setVendor} dark={dark} />
            <div className="label" style={{ margin: '14px 0 6px' }}>Date</div>
            <input
              type="date"
              value={date}
              max={window.kanjo?.ocr?.todayISO?.() || undefined}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Invoice date"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--rule)',
                background: 'var(--surface-2)', color: 'var(--ink)', fontSize: 15, boxSizing: 'border-box',
                colorScheme: dark ? 'dark' : 'light' }}
            />
            {dateFlagged && (
              <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.45, color: 'var(--warning)' }}>
                {dateClass.status === 'ancient'
                  ? <>That's over {Math.floor(dateClass.daysAgo / 30)} months ago — it would land outside every report. Check the year.</>
                  : <>That's {dateClass.daysAgo} days ago, more than a week back. You'll be asked to confirm it.</>}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 14 }}>
            <div style={{ padding: '12px 16px 4px' }}>
              <div className="label">Amount</div>
            </div>
            <TotalsRow label="Amount" cents={amountCents} fmt={fmt} dark={dark} strong onChange={setAmountCents}/>
            <TotalsRow label="Tax" cents={taxCents} fmt={fmt} dark={dark} onChange={setTaxCents}/>
            <TotalsRow label="Delivery fee" cents={freightCents} fmt={fmt} dark={dark} onChange={setFreightCents}/>
            <TotalsRow label="Total" cents={totalCents} fmt={fmt} strong readOnly/>
          </div>

          {saveError && <ErrorBanner style={{ marginTop: 12 }}>{saveError}</ErrorBanner>}
        </div>

        <div style={{ padding: '16px 20px 28px', borderTop: '1px solid var(--rule)', background: 'var(--bg)' }}>
          <button onClick={onSave} disabled={!canSave}
                  className="btn primary lg"
                  style={{ width: '100%', opacity: canSave ? 1 : 0.6, cursor: canSave ? 'pointer' : 'default' }}>
            {saving ? 'Saving…' : 'Save to books'}
          </button>
        </div>
      </div>

      {datePrompt && (
        <ConfirmDate
          dateStr={date}
          status={datePrompt.status}
          daysAgo={datePrompt.daysAgo}
          todayStr={todayStr}
          onToday={() => { setDate(todayStr); setDatePrompt(null); checkDuplicateThenPersist(todayStr); }}
          onKeep={() => { setDatePrompt(null); checkDuplicateThenPersist(); }}
          onFix={() => setDatePrompt(null)}
        />
      )}

      {dupePrompt && (
        <ConfirmDuplicate
          match={dupePrompt.match}
          kind={dupePrompt.kind}
          scan={{ vendorName, invoiceNumber: null, issuedDate: date, totalCents, itemCount: 0 }}
          fmt={fmt}
          onReplace={() => { setDupePrompt(null); persist({ replaceId: dupePrompt.match.invoiceId }); }}
          onDiscard={() => { setDupePrompt(null); nav.navigate('/receipts'); }}
          onCancel={() => setDupePrompt(null)}
        />
      )}
    </KShell>
  );
}

Object.assign(window, { ScanCamera: ScanCapture, ScanUpload: ScanCapture, ScanCapture, ScanProcessing, ScanConfirm, ScanDone, ManualEntry, scanReset });
